/**
 * Taken & logboek (US-016, US-026, US-029, US-032, design-handoff scherm 9).
 *
 * Bovenaan de laatste uitvoering met de tellers en de mislukte bronnen; daaronder de
 * uitvoeringen van de dagen ervoor. Een start buiten het venster van 00:15 wordt gemarkeerd.
 */

import {
  formatDuration,
  formatTimeNl,
  JOB_STATUS_LABELS,
  MEASUREMENT_STATUS_LABELS,
} from '@prijsmonitor/shared';
import type { JobStatus, MeasurementStatus } from '@prijsmonitor/shared';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiError, api } from '../api/client.js';
import { Button, Card, Chip, Counter, Notice, ui } from '../components/ui/index.js';
import { useSession } from '../session/SessionContext.js';
import styles from './TasksScreen.module.css';

interface Run {
  id: string;
  type: 'DAILY_CHECK' | 'MANUAL_RECHECK' | 'FEED_SYNC';
  status: JobStatus;
  scheduledFor: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  startedLate: boolean;
  plannedCount: number;
  successCount: number;
  emptyCount: number;
  errorCount: number;
  changeCount: number;
  /**
   * Wat de XML-feed in deze ronde deed (US-008).
   *
   * `gepland` is leeg bij een uitvoering van vóór deze telling: dan is het onbekend, en dat is iets
   * anders dan nul.
   */
  feed: { gepland: number | null; gelukt: number | null; melding: string | null };
  notes: string | null;
  emails: Array<{ kind: 'PRICE_CHANGES' | 'ERROR_SUMMARY'; status: string; error: string | null }>;
}

interface Failure {
  sourceId: string;
  sourceName: string;
  productId: string;
  productName: string;
  ownSku: string;
  status: MeasurementStatus;
  detail: string | null;
  busy: boolean;
  /**
   * Bewijs dat bij deze mislukking is vastgelegd (US-028).
   *
   * De crawler maakt bij een fout een schermafdruk, maar die was nergens te bereiken: het
   * productdetail had een knop, en dit is het scherm waar je kijkt als een taak faalt. Zonder dit
   * vulden die afdrukken de schijf zonder ooit gebruikt te worden.
   */
  bewijs: Array<{ id: string; soort: 'SCREENSHOT' | 'HTML_FRAGMENT'; kilobytes: number }>;
}

/** Vat samen wat er met de e-mail is gebeurd (US-024/026). */
function mailSummary(emails: Run['emails']): string {
  if (emails.length === 0) return '—';
  return emails
    .map((mail) => {
      const soort = mail.kind === 'PRICE_CHANGES' ? 'wijzigingsoverzicht' : 'foutoverzicht';
      switch (mail.status) {
        case 'SENT':
          return `${soort} verstuurd`;
        case 'SKIPPED_NOTHING_TO_REPORT':
          return `geen ${soort} (niets te melden)`;
        case 'NO_RECIPIENTS':
          return `${soort} niet verstuurd — geen ontvangers ingesteld`;
        default:
          return `${soort} MISLUKT`;
      }
    })
    .join(' · ');
}

/**
 * Vat samen wat de XML-feed heeft opgeleverd (US-008).
 *
 * Dit stond nergens. De totalen van een ronde zeggen er niets over: bij "11 gepland, 11 succesvol,
 * 0 fouten" is niet te zien of de eigen prijs erbij zat, en dus ook niet waarom hij ontbreekt in het
 * prijsoverzicht. De twee uitersten zijn allebei stil: een feed die niet is ingesteld levert een
 * melding op, maar géén eigen prijsbron in de ronde levert helemaal niets op.
 *
 * `null` betekent onbekend — een uitvoering van vóór deze telling. Dan hoort er geen regel te staan;
 * "0 bronnen" beweren zou onwaar zijn.
 */
function feedSummary(feed: Run['feed']): string | null {
  if (feed.gepland === null) return null;
  if (feed.melding) return `XML-feed niet gelezen — ${feed.melding}`;
  if (feed.gepland === 0) {
    return (
      'XML-feed: geen eigen prijsbron in deze ronde. De eigen prijs is dus niet gemeten; ' +
      'controleer of de producten op Actief staan.'
    );
  }
  const gelukt = feed.gelukt ?? 0;
  if (gelukt === feed.gepland) {
    return `XML-feed: ${gelukt} van ${feed.gepland} eigen prijzen gelezen.`;
  }
  return (
    `XML-feed: ${gelukt} van ${feed.gepland} eigen prijzen gelezen — ` +
    `${feed.gepland - gelukt} artikelnummer(s) leverden niets op.`
  );
}

/**
 * Kort in het logboek: alleen wanneer er met de feed iets aan de hand was.
 *
 * De volledige regel staat bij de laatste uitvoering; hier is de kolom smal. Wat je in een lijst
 * terug wil vinden is de ronde waarin de eigen prijs ontbrak, en niet de tientallen rondes waarin
 * hij gewoon is gelezen.
 */
function feedProbleem(feed: Run['feed']): string | null {
  if (feed.gepland === null) return null;
  if (feed.melding) return 'XML-feed niet gelezen';
  if (feed.gepland === 0) return 'geen eigen prijsbron';
  if ((feed.gelukt ?? 0) < feed.gepland) return 'eigen prijs deels gelezen';
  return null;
}

function tijd(waarde: string | null): string {
  return waarde ? formatTimeNl(new Date(waarde)) : '—';
}

export function TasksScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const { isAdmin } = useSession();

  const [runs, setRuns] = useState<Run[]>([]);
  const [totaal, setTotaal] = useState(0);
  /*
   * Hoeveel uitvoeringen het logboek toont.
   *
   * Het stond op zeven, en dat was in de praktijk vier kalenderdagen: een hercontrole of een met de
   * hand gestarte ronde levert een extra uitvoering op dezelfde dag op. Dertig is de standaard omdat
   * dat ongeveer een maand terugkijken is; wie verder terug moet, kan uitbreiden.
   */
  const [aantal, setAantal] = useState(30);
  const [failures, setFailures] = useState<Failure[]>([]);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ runs: Run[]; failures: Failure[]; totaal: number }>(
        `/api/jobs?aantal=${aantal}`,
      );
      setRuns(data.runs);
      setFailures(data.failures);
      setTotaal(data.totaal);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Het overzicht kon niet worden geladen.');
    }
  }, [aantal]);

  useEffect(() => {
    void load();
  }, [load]);

  const loopt = runs[0]?.status === 'RUNNING';

  /**
   * Zolang er een controle loopt het overzicht bijwerken. Een ronde duurt minuten en de teller
   * loopt pas aan het eind bij, dus dit is de manier om te zien dat hij klaar is.
   */
  useEffect(() => {
    if (!loopt) return;
    const timer = setInterval(() => void load(), 5_000);
    return () => clearInterval(timer);
  }, [loopt, load]);

  async function startControle(): Promise<void> {
    setBusy('start');
    setError(null);
    setNotice(null);
    try {
      await api.post('/api/jobs/uitvoeren');
      setNotice('De controle is gestart. Dit kan enkele minuten duren.');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'De controle kon niet worden gestart.');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Stopt een controle die blijft hangen.
   *
   * Nodig na een herstart of een deploy halverwege een ronde: het proces dat de controle
   * uitvoerde bestaat dan niet meer, maar de ronde staat in de database nog op "bezig" en er is
   * verder niemand die dat record ooit afsluit. Zonder deze knop bleef "Nu controleren" voorgoed
   * uitgeschakeld.
   */
  async function stopControle(): Promise<void> {
    setBusy('stop');
    setError(null);
    setNotice(null);
    try {
      await api.post('/api/jobs/annuleren');
      setNotice('De controle is gestopt. Je kunt nu opnieuw starten.');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'De controle kon niet worden gestopt.');
    } finally {
      setBusy(null);
    }
  }

  async function hercontroleer(sourceId: string): Promise<void> {
    setBusy(sourceId);
    setError(null);
    try {
      await api.post(`/api/sources/${sourceId}/hercontroleren`);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'De hercontrole is niet gelukt.');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Controleert alle mislukte bronnen uit het overzicht in één keer.
   *
   * Zonder dit moest je na bijvoorbeeld een ronde door de bronconfigurator elke mislukte bron
   * apart aanklikken, of alles — inclusief de bronnen die al goed stonden — opnieuw laten lopen
   * via "Nu controleren". De server start dit op de achtergrond (net als "Nu controleren"); dit
   * scherm volgt de voortgang via de bestaande polling zodra de nieuwe uitvoering "loopt" wordt.
   */
  async function hercontroleerMislukte(): Promise<void> {
    setBusy('mislukte');
    setError(null);
    setNotice(null);
    try {
      await api.post('/api/jobs/mislukte/hercontroleren', {
        sourceIds: failures.map((failure) => failure.sourceId),
      });
      setNotice('De mislukte bronnen worden opnieuw gecontroleerd. Dit kan enkele minuten duren.');
      await load();
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : 'De mislukte bronnen konden niet worden gecontroleerd.',
      );
    } finally {
      setBusy(null);
    }
  }

  const laatste = runs[0];

  return (
    <div className={ui.page}>
      {error && <Notice tone="error">{error}</Notice>}
      {notice && <Notice tone="success">{notice}</Notice>}

      {!laatste ? (
        <Card
          title="Dagelijkse controle"
          actions={
            isAdmin && (
              <Button
                variant="primary"
                busy={busy === 'start'}
                onClick={() => void startControle()}
              >
                Nu controleren
              </Button>
            )
          }
        >
          <p className={ui.empty}>
            Er is nog geen controle uitgevoerd. De eerste ronde draait vannacht om 00:15.
          </p>
        </Card>
      ) : (
        <Card
          title="Dagelijkse controle · laatste uitvoering"
          description={`Gepland om ${tijd(laatste.scheduledFor)}, gestart om ${tijd(laatste.startedAt)} en geëindigd om ${tijd(laatste.finishedAt)} (${formatDuration(laatste.durationMs)}).`}
          actions={
            <div className={styles.headActions}>
              <Chip tone={laatste.status === 'COMPLETED' ? 'success' : 'warn'}>
                {JOB_STATUS_LABELS[laatste.status]}
              </Chip>
              {isAdmin && loopt && (
                <Button variant="danger" busy={busy === 'stop'} onClick={() => void stopControle()}>
                  Stoppen
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="primary"
                  busy={busy === 'start'}
                  disabled={loopt}
                  onClick={() => void startControle()}
                >
                  {loopt ? 'Bezig…' : 'Nu controleren'}
                </Button>
              )}
            </div>
          }
        >
          {loopt && (
            <Notice tone="info">
              <span className={styles.spinner} aria-hidden="true" /> De controle loopt —{' '}
              {laatste.plannedCount} bron(nen) worden nagekeken. Dit scherm werkt zichzelf bij.
            </Notice>
          )}
          {laatste.startedLate && (
            <Notice tone="warn">
              ⚠ Deze uitvoering startte later dan het geplande tijdstip van 00:15.
            </Notice>
          )}

          <div className={ui.counterRow} style={{ marginTop: 'var(--space-12)' }}>
            <Counter value={laatste.plannedCount} label="Gepland" />
            <Counter value={laatste.successCount} label="Succesvol" />
            <Counter
              value={laatste.emptyCount}
              label="Lege prijzen / fouten"
              {...(laatste.emptyCount > 0 ? { tone: 'warn' as const } : {})}
            />
            <Counter
              value={laatste.changeCount}
              label="Prijswijzigingen"
              {...(laatste.changeCount > 0 ? { tone: 'danger' as const } : {})}
            />
          </div>

          {feedSummary(laatste.feed) !== null && (
            <p
              className={styles.mailLine}
              {...(laatste.feed.melding || laatste.feed.gepland === 0
                ? { 'data-let-op': 'ja' }
                : {})}
            >
              {feedSummary(laatste.feed)}
            </p>
          )}

          <p className={styles.mailLine}>E-mail: {mailSummary(laatste.emails)}</p>

          {failures.length > 0 && (
            <div className={styles.failures}>
              <div className={styles.failuresKop}>
                <button
                  type="button"
                  className={styles.disclosure}
                  onClick={() => setOpen((v) => !v)}
                  aria-expanded={open}
                >
                  {open ? '▾' : '▸'} {failures.length} mislukte{' '}
                  {failures.length === 1 ? 'bron' : 'bronnen'}
                </button>
                {isAdmin && (
                  <Button
                    variant="secondary"
                    busy={busy === 'mislukte'}
                    disabled={loopt || failures.every((failure) => failure.busy)}
                    onClick={() => void hercontroleerMislukte()}
                  >
                    Controleer de mislukte
                  </Button>
                )}
              </div>

              {open && (
                <div className={ui.tableWrap}>
                  <table className={ui.table}>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Bron</th>
                        <th>Status</th>
                        <th>Toelichting</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {failures.map((failure) => (
                        <tr key={failure.sourceId}>
                          <td>
                            <button
                              type="button"
                              className={styles.linkButton}
                              onClick={() => void navigate(`/product/${failure.productId}`)}
                            >
                              {failure.productName}
                            </button>
                            <div className={styles.sku}>{failure.ownSku}</div>
                          </td>
                          <td>{failure.sourceName}</td>
                          <td>
                            <Chip tone="warn">{MEASUREMENT_STATUS_LABELS[failure.status]}</Chip>
                          </td>
                          <td className={styles.detail}>
                            {failure.detail ?? ''}
                            {failure.bewijs.length > 0 && (
                              <div className={styles.bewijsrij}>
                                {failure.bewijs.map((stuk) => (
                                  <a
                                    key={stuk.id}
                                    className={styles.bewijslink}
                                    href={`/api/evidence/${stuk.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {stuk.soort === 'SCREENSHOT'
                                      ? `Schermafdruk (${stuk.kilobytes} kB)`
                                      : `Paginabron downloaden (${stuk.kilobytes} kB)`}
                                  </a>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className={styles.acties}>
                            {isAdmin && (
                              <>
                                <Button
                                  onClick={() => void hercontroleer(failure.sourceId)}
                                  busy={busy === failure.sourceId}
                                  disabled={failure.busy}
                                >
                                  {failure.busy ? 'Bezig…' : 'Opnieuw controleren'}
                                </Button>
                                {/*
                                   Rechtstreeks naar de bronconfigurator, met dit product en deze bron
                                   al gekozen (US-011 t/m US-015). Zonder dit moest je bij elke fout
                                   eerst het product en dan, tussen alle webshops, de juiste bron weer
                                   opzoeken — bij tientallen mislukte bronnen tegelijk was dat het
                                   grootste deel van de tijd die een bronconfiguratie-ronde kostte.
                                */}
                                <Button
                                  variant="secondary"
                                  onClick={() =>
                                    void navigate(
                                      `/bronconfigurator?product=${failure.productId}&source=${failure.sourceId}`,
                                    )
                                  }
                                >
                                  Bron configureren
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {runs.length > 1 && (
        <Card
          title="Eerdere uitvoeringen"
          /*
             Het aantal erbij, en hoeveel er in totaal zijn. Een afgekapte lijst is anders niet van
             een volledige te onderscheiden — en dan lijkt het of er niet meer is.
          */
          description={
            totaal > runs.length
              ? `${runs.length - 1} van ${totaal - 1} eerdere uitvoeringen`
              : `alle ${runs.length - 1} eerdere uitvoeringen`
          }
          actions={
            /*
               Getallen en geen "Alles": 365 is de bovengrens van de route, dus "Alles" zou onwaar
               worden zodra er meer uitvoeringen zijn. De regel erboven zegt hoeveel er in totaal
               zijn, dus dat verschil blijft zichtbaar.
            */
            <div className={styles.aantalKeuze} role="group" aria-label="Aantal uitvoeringen">
              {[30, 90, 365].map((optie) => (
                <button
                  key={optie}
                  type="button"
                  className={aantal === optie ? styles.aantalActief : styles.aantalKnop}
                  onClick={() => setAantal(optie)}
                >
                  {optie}
                </button>
              ))}
            </div>
          }
        >
          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>Gepland</th>
                  <th>Start</th>
                  <th>Einde</th>
                  <th>Duur</th>
                  <th className={ui.numeric}>Succes</th>
                  <th className={ui.numeric}>Fouten</th>
                  <th className={ui.numeric}>Wijzigingen</th>
                  <th>Opmerkingen</th>
                </tr>
              </thead>
              <tbody>
                {runs.slice(1).map((run) => (
                  <tr key={run.id}>
                    <td>{new Date(run.scheduledFor).toLocaleDateString('nl-NL')}</td>
                    <td className={run.startedLate ? styles.lateStart : undefined}>
                      {tijd(run.startedAt)}
                      {run.startedLate && ' ⚠'}
                    </td>
                    <td>{tijd(run.finishedAt)}</td>
                    <td>{formatDuration(run.durationMs)}</td>
                    <td className={ui.numeric}>{run.successCount}</td>
                    <td className={ui.numeric}>{run.errorCount}</td>
                    <td className={ui.numeric}>{run.changeCount}</td>
                    <td className={styles.detail}>
                      {[feedProbleem(run.feed), run.notes ?? mailSummary(run.emails)]
                        .filter(Boolean)
                        .join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
